import type { PaintingService } from '../types/content';

export const paintingServices: PaintingService[] = [
  // ── Commercial ───────────────────────────────────────────────
  {
    id: 'com-interior',
    market: 'commercial',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/8b3d5ec3-962a-4d70-bc72-88c3a95c09c4.jpg',
    alt: {
      en: 'Professional commercial interior painting in a Columbus, Ohio office',
      es: 'Pintura comercial interior profesional en una oficina de Columbus, Ohio',
    },
    title: { en: 'Interior Commercial Painting', es: 'Pintura Comercial Interior' },
    description: {
      en: 'Professional interior painting for offices, warehouses, retail spaces, schools, and more. Expert surface preparation and finishes.',
      es: 'Pintura interior profesional para oficinas, almacenes, comercios, escuelas y más. Preparación de superficies y acabados expertos.',
    },
    points: [
      { en: 'Office repainting', es: 'Repintado de oficinas' },
      { en: 'Warehouse interiors', es: 'Interiores de almacenes' },
      { en: 'Retail & industrial', es: 'Comercios e industrias' },
      { en: 'Color consultation', es: 'Asesoría de color' },
    ],
    more: {
      en: 'Lobbies, hallways, conference rooms, classrooms, patient areas, and apartment units — painted with low-odor, low-VOC paints on nights or weekends so your business keeps running.',
      es: 'Vestíbulos, pasillos, salas de juntas, aulas, consultorios y apartamentos — con pinturas de bajo olor y bajo VOC, de noche o en fin de semana para que su negocio siga operando.',
    },
  },
  {
    id: 'com-exterior',
    market: 'commercial',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/959278f5-8e1e-4fb5-b7ef-816e5ff207de.jpg',
    alt: {
      en: 'Commercial exterior painting of a building facade in Columbus, Ohio',
      es: 'Pintura exterior comercial de la fachada de un edificio en Columbus, Ohio',
    },
    title: { en: 'Exterior Commercial Painting', es: 'Pintura Comercial Exterior' },
    description: {
      en: 'Professional exterior painting for commercial buildings, storefronts, and industrial properties. Durable, weather-resistant finishes.',
      es: 'Pintura exterior profesional para edificios comerciales, fachadas de tiendas y propiedades industriales. Acabados duraderos y resistentes al clima.',
    },
    points: [
      { en: 'Building facades', es: 'Fachadas de edificios' },
      { en: 'Storefront refreshes', es: 'Renovación de tiendas' },
      { en: 'Industrial exteriors', es: 'Exteriores industriales' },
      { en: 'Protective coatings', es: 'Recubrimientos protectores' },
    ],
    more: {
      en: 'We pressure-wash, scrape, caulk, and prime before applying paint systems rated for Ohio freeze-thaw cycles on masonry, stucco, siding, and metal.',
      es: 'Lavamos a presión, raspamos, sellamos y aplicamos primer antes de pintar con sistemas diseñados para los ciclos de congelación de Ohio en ladrillo, estuco, revestimiento y metal.',
    },
  },
  {
    id: 'com-drywall',
    market: 'commercial',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/127c5cbc-832b-44cd-af6a-929a84d26000.jpg',
    alt: {
      en: 'Commercial drywall repair and surface preparation before painting in Columbus, Ohio',
      es: 'Reparación de drywall y preparación de superficies antes de pintar en Columbus, Ohio',
    },
    title: { en: 'Drywall Repair & Surface Preparation', es: 'Reparación de Drywall y Preparación' },
    description: {
      en: 'Expert drywall repair and comprehensive surface preparation for flawless painting results.',
      es: 'Reparación experta de drywall y preparación completa de superficies para un acabado impecable.',
    },
    points: [
      { en: 'Drywall repair', es: 'Reparación de drywall' },
      { en: 'Surface preparation', es: 'Preparación de superficies' },
      { en: 'Sanding & finishing', es: 'Lijado y acabado' },
      { en: 'Patch work', es: 'Resanes y parches' },
    ],
    more: {
      en: 'Holes, cracks, water damage, and dents are patched, taped, texture-matched, sanded, and primed — including new construction and renovation prep.',
      es: 'Reparamos agujeros, grietas, daños por agua y golpes: resanamos, encintamos, igualamos la textura, lijamos y aplicamos primer — también en obra nueva y remodelaciones.',
    },
  },
  {
    id: 'com-specialty',
    market: 'commercial',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/65bd2267-3330-48dd-91a3-01d0a6bbb44b.jpg',
    alt: {
      en: 'Warehouse painting and epoxy floor coating in Columbus, Ohio',
      es: 'Pintura de almacén y piso epóxico en Columbus, Ohio',
    },
    title: { en: 'Specialty Painting & Coatings', es: 'Pintura y Recubrimientos Especiales' },
    description: {
      en: 'Protective coatings, waterproofing, epoxy flooring, and specialty finishes.',
      es: 'Recubrimientos protectores, impermeabilización, pisos epóxicos y acabados especiales.',
    },
    points: [
      { en: 'Protective coatings', es: 'Recubrimientos protectores' },
      { en: 'Waterproofing', es: 'Impermeabilización' },
      { en: 'Epoxy floors', es: 'Pisos epóxicos' },
      { en: 'High-ceiling painting', es: 'Pintura en techos altos' },
    ],
    more: {
      en: 'Warehouse walls up to 40 ft, steel columns, safety striping, elastomeric and anti-graffiti coatings, plus post-construction cleanup of paint debris.',
      es: 'Paredes de almacén de hasta 40 pies, columnas de acero, líneas de seguridad, recubrimientos elastoméricos y antigraffiti, y limpieza post-construcción de residuos de pintura.',
    },
  },

  // ── Residential ──────────────────────────────────────────────
  {
    id: 'res-interior',
    market: 'residential',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/01c77263-f704-457a-b9c8-ee2a06c932ea.jpg',
    alt: {
      en: 'Interior house painting of a bedroom in Columbus, Ohio',
      es: 'Pintura interior residencial de una habitación en Columbus, Ohio',
    },
    title: { en: 'Interior Residential Painting', es: 'Pintura Residencial Interior' },
    description: {
      en: 'Professional interior painting for your home. Whole house, room-by-room, or accent walls. Expert color consultation and premium finishes.',
      es: 'Pintura interior profesional para su hogar. Toda la casa, habitación por habitación o paredes de acento. Asesoría de color y acabados premium.',
    },
    points: [
      { en: 'Whole house painting', es: 'Pintura de toda la casa' },
      { en: 'Bedroom & living room', es: 'Recámaras y sala' },
      { en: 'Kitchen & bathroom', es: 'Cocina y baño' },
      { en: 'Ceilings, trim, doors', es: 'Techos, molduras y puertas' },
      { en: 'Cabinet painting', es: 'Pintura de gabinetes' },
      { en: 'Custom color matching', es: 'Igualación de colores' },
    ],
    more: {
      en: 'We move and cover furniture, mask floors, fill nail holes, and cut clean lines at ceilings and trim. Basements and garages too — and we clean up every day before we leave.',
      es: 'Movemos y cubrimos los muebles, protegemos los pisos, rellenamos agujeros y marcamos líneas limpias en techos y molduras. También sótanos y garajes — y limpiamos cada día antes de irnos.',
    },
  },
  {
    id: 'res-exterior',
    market: 'residential',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/1278f6d6-372e-4bb1-8ffd-f87f8e07caca.jpg',
    alt: {
      en: 'Exterior house painting of siding and trim in Columbus, Ohio',
      es: 'Pintura exterior de casa, revestimiento y molduras en Columbus, Ohio',
    },
    title: { en: 'Exterior Residential Painting', es: 'Pintura Residencial Exterior' },
    description: {
      en: "Professional exterior painting to enhance your home's curb appeal. Siding, trim, doors, decks, and fencing painted with premium finishes.",
      es: 'Pintura exterior profesional para realzar la fachada de su casa. Revestimiento, molduras, puertas, terrazas y cercas con acabados premium.',
    },
    points: [
      { en: 'House exterior repainting', es: 'Repintado exterior de casas' },
      { en: 'Siding & trim', es: 'Revestimiento y molduras' },
      { en: 'Garage & entrance doors', es: 'Puertas de garaje y entrada' },
      { en: 'Deck & fence staining', es: 'Tinte de terrazas y cercas' },
      { en: 'Shutters', es: 'Contraventanas' },
      { en: 'Weather-resistant finishes', es: 'Acabados resistentes al clima' },
    ],
    more: {
      en: 'Every exterior starts with washing, scraping, caulking, and priming, then premium exterior paint built to handle Ohio winters and summer sun — while protecting your landscaping.',
      es: 'Cada exterior comienza con lavado, raspado, sellado y primer, seguido de pintura exterior premium hecha para los inviernos y el sol de Ohio — protegiendo su jardín.',
    },
  },
  {
    id: 'res-specialty',
    market: 'residential',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/96e7a0d2-23fc-4166-838b-d743b4bfb1df.jpg',
    alt: {
      en: 'Kitchen cabinet painting and refinishing in Columbus, Ohio',
      es: 'Pintura y renovación de gabinetes de cocina en Columbus, Ohio',
    },
    title: { en: 'Specialty Home Painting', es: 'Pintura Especial para el Hogar' },
    description: {
      en: 'Cabinet refinishing, door & trim painting, basement & garage painting, and more. Premium finishes for every room.',
      es: 'Renovación de gabinetes, pintura de puertas y molduras, sótanos y garajes, y más. Acabados premium para cada espacio.',
    },
    points: [
      { en: 'Cabinet refinishing', es: 'Renovación de gabinetes' },
      { en: 'Door & trim painting', es: 'Puertas y molduras' },
      { en: 'Basement & garage', es: 'Sótanos y garajes' },
      { en: 'Stain & varnish work', es: 'Tinte y barniz' },
      { en: 'Texture services', es: 'Servicios de textura' },
      { en: 'Drywall repair', es: 'Reparación de drywall' },
    ],
    more: {
      en: 'Cabinet doors are removed, degreased, sanded, primed, and sprayed for a smooth factory-like finish. We also remove or add texture and refinish stained woodwork.',
      es: 'Desmontamos, desengrasamos, lijamos, aplicamos primer y pintamos con pistola las puertas de los gabinetes para un acabado liso de fábrica. También quitamos o aplicamos textura y renovamos madera barnizada.',
    },
  },
];
